import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useAppConfig } from '@/context/AppConfigContext';
import Navbar from '../components/Navbar';
import PostCard from '../components/PostCard';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { User, Lock, Bookmark, Crown, FileText, Users, MessageCircle, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MyPostSkeleton from '@/components/MyPostSkeleton';
import SavedPostSkeleton from '@/components/SavedPostSkeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [savedPosts, setSavedPosts] = useState([]);
  const [myPosts, setMyPosts] = useState([]);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({ old_password: '', new_password: '' });
  const [loading, setLoading] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [postStats, setPostStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [myPostsLoading, setMyPostsLoading] = useState(true);
  const [savedPostsLoading, setSavedPostsLoading] = useState(true);
  const [myPostsCursor, setMyPostsCursor] = useState(null);
  const [hasMoreMyPosts, setHasMoreMyPosts] = useState(true);
  const [loadingMoreMyPosts, setLoadingMoreMyPosts] = useState(false);
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  


  // Load my posts
  useEffect(() => {
    if (!token) return;
    loadMyPosts(true);
  }, [token]);

  // Load saved posts
  useEffect(() => {
    if (!token) return;
    loadSavedPosts();
  }, [token]);

  const loadSavedPosts = async () => {
    setSavedPostsLoading(true);
    try {
      const res = await axios.get(`${API}/posts/saved`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedPosts(res.data.posts || []);
    } catch (err) {
      console.error('Error loading saved posts:', err);
    } finally {
      setSavedPostsLoading(false);
    }
  };


// ++++
  const { isSeasonalTheme } = useAppConfig()
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem('theme') === 'dark'
  )

  useEffect(() => {
    // Don't toggle dark mode when a seasonal theme is active
    // (AppConfigContext manages dark mode during themed periods)
    if (isSeasonalTheme) return
    const root = document.documentElement
    if (darkMode) {
      root.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    } else {
      root.classList.remove('dark')
      localStorage.setItem('theme', 'light')
    }
  }, [darkMode, isSeasonalTheme])

// ++++

  const loadMyPosts = async (initial = false) => {
    if (!hasMoreMyPosts && !initial) return;

    initial ? setMyPostsLoading(true) : setLoadingMoreMyPosts(true);

    try {
      const res = await axios.get(`${API}/posts/my-posts`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          limit: 10,
          before: initial ? null : myPostsCursor,
        },
      });

      const newPosts = res.data.posts || [];

      setMyPosts(prev =>
        initial ? newPosts : [...prev, ...newPosts]
      );

      setMyPostsCursor(res.data.next_cursor || null);
      setHasMoreMyPosts(newPosts.length === 10);

    } catch (err) {
      console.error('Error loading my posts:', err);
    } finally {
      setMyPostsLoading(false);
      setLoadingMoreMyPosts(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${API}/profile/password`,
        passwordData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password updated successfully');
      setShowPasswordDialog(false);
      setPasswordData({ old_password: '', new_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsave = async (postId) => {
    try {
      await axios.delete(`${API}/posts/${postId}/save`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post removed from saved');
      loadSavedPosts();
    } catch (err) {
      toast.error('Failed to unsave post');
    }
  };

  const handleViewPost = async (postId) => {
    setSelectedPost(postId);
    setStatsLoading(true);

    try {
      const res = await axios.get(
        `${API}/posts/${postId}/stats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setPostStats(res.data);
    } catch (err) {
      alert("Failed to load post stats");
    } finally {
      setStatsLoading(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("This will permanently delete the post. Continue?"))
      return;

    try {
      await axios.delete(
        `${API}/posts/${postId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // remove from UI immediately
      setMyPosts((prev) => prev.filter((p) => p.id !== postId));
    } catch (err) {
      alert("Failed to delete post");
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeletingAccount(true);
    try {
      await axios.delete(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Your account has been permanently deleted.');
      logout();
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete account');
      setDeletingAccount(false);
    }
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const postTime = new Date(timestamp);
    const diff = Math.floor((now - postTime) / 1000);

    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const isPremium = user?.role === 'premium';

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + window.scrollY >=
          document.body.offsetHeight - 300 &&
        hasMoreMyPosts &&
        !loadingMoreMyPosts &&
        !myPostsLoading
      ) {
        loadMyPosts(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMoreMyPosts, loadingMoreMyPosts, myPostsLoading]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8" data-testid="profile-page">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 page-header-center">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100 mb-2">Profile</h1>
            <p className="text-gray-600 dark:text-slate-400">Manage your account and view your activity</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Profile Info */}
            <div className="lg:col-span-1 space-y-6 self-start">
              {/* User Card */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-700 p-6">
                <div className="text-center">
                  <img
                    src={user?.avatar_url}
                    alt={user?.username}
                    className={`w-24 h-24 rounded-full mx-auto mb-4 ${isPremium ? 'ring-4 ring-purple-400' : ''}`}
                  />
                  <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-1 flex items-center justify-center gap-2">
                    {user?.username}
                    {isPremium && <Crown className="w-5 h-5 text-yellow-500" />}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">{user?.email}</p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-slate-400">Status:</span>
                      <span className={`font-medium ${
                        user?.status === 'active' ? 'text-green-600' :
                        user?.status === 'banned' ? 'text-red-600' :
                        'text-yellow-600'
                      }`}>
                        {user?.status === 'active' ? 'Active' :
                         user?.status === 'banned' ? 'Banned' :
                         'Shadow Banned'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-slate-400">Account Type:</span>
                      <span className="font-medium text-gray-900 dark:text-slate-100">
                        {isPremium ? '⭐ Premium' : 'Free'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-slate-400">Credits:</span>
                      <span className="font-medium text-gray-900 dark:text-slate-100">{user?.credits || 0}</span>
                    </div>
                  </div>

                  {!isPremium && (
                    <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200 mb-4">
                      <Crown className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                      <p className="text-sm text-purple-900 font-medium mb-2">Upgrade to Premium</p>
                      <ul className="text-xs text-purple-700 space-y-1 mb-3">
                        <li>✓ 15 posts per day</li>
                        <li>✓ 6 reports per day</li>
                        <li>✓ Save up to 30 posts</li>
                        <li>✓ Auto-promoted posts</li>
                      </ul>
                      <Button
                        data-testid="upgrade-premium-btn"
                        onClick={() => navigate('/payment')}
                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                        size="sm"
                      >
                        Upgrade Now
                      </Button>
                    </div>
                  )}

                  <Button
                    data-testid="change-password-btn"
                    onClick={() => setShowPasswordDialog(true)}
                    variant="outline"
                    className="w-full border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </Button>
                  {!isSeasonalTheme && (
                  <Button
                    onClick={() => setDarkMode(prev => !prev)}
                    variant="outline"
                    className="w-full mt-3 flex items-center justify-between border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700"
                  >
                    <span>Theme</span>
                    <span className="text-sm opacity-80">
                      {darkMode ? 'Dark' : 'Light'}
                    </span>
                  </Button>
                  )}
                  {isSeasonalTheme && (
                  <div className="w-full mt-3 px-4 py-2.5 rounded-lg border border-dashed border-gray-300 text-center text-xs text-gray-400">
                    🎨 Seasonal theme active — dark mode locked
                  </div>
                  )}

                  <Button
                    onClick={() => {
                      setDeleteConfirmText('');
                      setShowDeleteAccountDialog(true);
                    }}
                    variant="outline"
                    className="w-full mt-3 border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Account
                  </Button>
                </div>
              </div>
            </div>

            {/* Posts Tabs */}
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-700 p-6">
                <Tabs defaultValue="my-posts" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-6 bg-white/60 dark:bg-slate-800/60 backdrop-blur-md border border-gray-200 dark:border-slate-700 rounded-full p-1">
                    <TabsTrigger value="my-posts" data-testid="my-posts-tab" className="rounded-full flex items-center justify-center gap-2 text-gray-700 dark:text-slate-200 transition-all duration-300 data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-700 dark:data-[state=active]:bg-cyan-400/10 dark:data-[state=active]:text-cyan-300 data-[state=active]:shadow-inner hover:bg-cyan-500/10 dark:hover:bg-cyan-400/5">
                      <FileText className="w-4 h-4 mr-2 sm:block"/>
                      <span className="whitespace-nowrap">
                        My Posts ({myPostsLoading ? 'Loading...' : myPosts.length})
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="saved" data-testid="saved-posts-tab" className="rounded-full flex items-center justify-center gap-2 text-gray-700 dark:text-slate-200 transition-all duration-300 data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-700 dark:data-[state=active]:bg-cyan-400/10 dark:data-[state=active]:text-cyan-300 data-[state=active]:shadow-inner hover:bg-cyan-500/10 dark:hover:bg-cyan-400/5">
                      <Bookmark className="w-4 h-4 mr-2 sm:block" />
                      <span className="whitespace-nowrap">
                        Saved ({savedPostsLoading ? '...' : savedPosts.length}/{isPremium ? 30 : 3})
                      </span>
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="my-posts" className="space-y-4">
                    {myPostsLoading ? (
                      <>
                        <MyPostSkeleton />
                        <MyPostSkeleton />
                      </>
                    ) : myPosts.length > 0 ? (
                      <>
                        {myPosts.map((post) => (
                          <div
                            key={post.id}
                            className="p-4 border border-gray-200 dark:border-slate-700 rounded-xl hover:border-cyan-300 transition-colors"
                          >
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <p className="text-gray-900 dark:text-slate-100 flex-1 break-all whitespace-pre-wrap overflow-hidden max-w-full line-clamp-4">
                                {post.content}
                              </p>

                              {post.promoted && (
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                                  ⚡
                                </span>
                              )}
                            </div>

                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-500 dark:text-slate-400">
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {post.vote_count || 0} votes
                                </span>

                                <span className="flex items-center gap-1">
                                  <MessageCircle className="w-3 h-3" />
                                  {post.comment_count || 0} comments
                                </span>

                                <span>{getTimeAgo(post.created_at)}</span>
                              </div>

                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleViewPost(post.id)}
                                  className="px-3 py-1 text-xs rounded-md border border-gray-200 dark:border-slate-700 hover:bg-gray-100 transition"
                                >
                                  View
                                </button>

                                <button
                                  onClick={() => handleDeletePost(post.id)}
                                  className="px-3 py-1 text-xs rounded-md border border-red-200 text-red-600 hover:bg-red-50 transition"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}

                        {/* 👇 THIS IS THE LINE YOU ASKED ABOUT */}
                        {loadingMoreMyPosts && <MyPostSkeleton />}
                      </>
                    ) : (
                      <div className="text-center py-20">
                        <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-600 dark:text-slate-400 text-lg">
                          No posts yet
                        </p>
                        <p className="text-gray-500 text-sm mt-2">
                          Create your first post to see it here
                        </p>
                        <Button onClick={() => navigate('/submit')} className="mt-4">
                          Create Post
                        </Button>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="saved">
                    {savedPostsLoading ? (
                      <>
                        <SavedPostSkeleton/>
                        <SavedPostSkeleton/>
                      </>
                    ) : savedPosts.length > 0 ? (
                      <div className="space-y-6">
                        {savedPosts.map((post) => (
                          <div key={post.id} className="relative w-full overflow-hidden rounded-2xl border border-gray-200 dark:border-slate-700 hover:border-cyan-300 transition-colors">
                            {!post.accessible && (
                              <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm z-10 rounded-2xl flex items-center justify-center">
                                <div className="text-center p-6">
                                  <Lock className="w-12 h-12 text-white mx-auto mb-3" />
                                  <p className="text-white font-medium mb-2">Premium Required</p>
                                  <Button
                                    onClick={() => navigate('/payment')}
                                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                                  >
                                    Upgrade to Access
                                  </Button>
                                </div>
                              </div>
                            )}
                            <div className="w-full max-w-full overflow-hidden">
                            <PostCard
                              post={post}
                              compact
                              onSkip={() => {}}
                              isSaved={true}
                              onUnsave={() => handleUnsave(post.id)}
                            />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-20">
                        <Bookmark className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-600 dark:text-slate-400 text-lg">No saved posts yet</p>
                        <p className="text-gray-500 text-sm mt-2">Save posts to access them later</p>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Dialog
        open={!!selectedPost}
        onOpenChange={() => {
          setSelectedPost(null);
          setPostStats(null);
        }}
      >
        <DialogContent className="max-w-2xl dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="dark:text-white">Post Details</DialogTitle>
          </DialogHeader>

          {statsLoading ? (
            <p className="text-center py-8 text-gray-500">Loading…</p>
          ) : postStats ? (
            <div className="space-y-4">
              {/* Post content */}
              <p className="text-gray-900 dark:text-slate-100 whitespace-pre-wrap p-4 bg-gray-50 dark:bg-slate-700 rounded-xl leading-relaxed">
                {postStats.post.content}
              </p>

              {/* Vote bars */}
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-700 dark:text-slate-300">Votes ({postStats.votes.total} total)</p>
                {[
                  { label: 'Agree', val: postStats.votes.agree, pct: postStats.votes.agree_percent, color: 'bg-green-500', text: 'text-green-600' },
                  { label: 'Disagree', val: postStats.votes.disagree, pct: postStats.votes.disagree_percent, color: 'bg-red-500', text: 'text-red-600' },
                  { label: 'Neutral', val: postStats.votes.neutral, pct: postStats.votes.neutral_percent, color: 'bg-gray-400', text: 'text-gray-500' },
                ].map(({ label, val, pct, color, text }) => (
                  <div key={label}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className={`${text} font-medium`}>{label} ({val})</span>
                      <span className="font-semibold dark:text-white">{pct ?? 0}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden">
                      <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct ?? 0}%` }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Comments */}
              <div>
                <p className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                  Comments ({postStats.comments.length})
                </p>
                <div className="space-y-2 max-h-52 overflow-y-auto">
                  {postStats.comments.length > 0 ? (
                    postStats.comments.map((c) => (
                      <div key={c.id} className="flex gap-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-xl">
                        <img src={c.avatar_url} className="w-8 h-8 rounded-full flex-shrink-0" alt={c.username} />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{c.username}</p>
                          <p className="text-sm text-gray-600 dark:text-slate-300 whitespace-pre-wrap">{c.content}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No comments yet</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center text-red-500">Failed to load post</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Password Update Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePasswordUpdate} className="space-y-4">
            <div>
              <Label htmlFor="old_password">Current Password</Label>
              <Input
                id="old_password"
                type="password"
                data-testid="old-password"
                value={passwordData.old_password}
                onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                data-testid="new-password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                required
              />
            </div>
            <Button type="submit" data-testid="update-password-btn" className="w-full" disabled={loading}>
              {loading ? 'Updating...' : 'Update Password'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={showDeleteAccountDialog} onOpenChange={(open) => {
        setShowDeleteAccountDialog(open);
        if (!open) setDeleteConfirmText('');
      }}>
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-red-600 dark:text-red-400 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete Account
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-300 font-medium mb-2">
                ⚠️ This action is permanent and cannot be undone.
              </p>
              <ul className="text-sm text-red-700 dark:text-red-400 space-y-1">
                <li>• All your posts will be deleted</li>
                <li>• All your votes and comments will be removed</li>
                <li>• Your account and email cannot be recovered</li>
                <li>• Any active premium subscription will not be refunded</li>
              </ul>
            </div>

            <div>
              <Label htmlFor="delete-confirm" className="text-sm text-gray-700 dark:text-slate-300">
                Type <span className="font-mono font-bold text-red-600">DELETE</span> to confirm
              </Label>
              <Input
                id="delete-confirm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                className="mt-1 font-mono text-gray-900 dark:text-white"
                autoComplete="off"
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowDeleteAccountDialog(false);
                  setDeleteConfirmText('');
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                disabled={deleteConfirmText !== 'DELETE' || deletingAccount}
                onClick={handleDeleteAccount}
              >
                {deletingAccount ? 'Deleting...' : 'Permanently Delete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profile;